import type { Ref } from "react";
import { Box, Checkbox, Chip, FormControlLabel, IconButton, Tooltip, Typography } from "@mui/material";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SearchIcon from "@mui/icons-material/Search";
import SearchBox from "@/components/ui/SearchBox";
import { APP_ACTIVE_TOOL_COLOR } from "@/theme";
import { SECTION_FILTER_CHIPS, VIEW_ALL_CHIP, type SectionFilter } from "./libraryItems";

export interface LibraryFilterBarProps {
  readonly sectionFilter: SectionFilter;
  readonly onSectionFilterChange: (value: SectionFilter) => void;
  readonly contextBarDescription: string;
  readonly activeShotBackTooltip: string;
  readonly approvedForProjectOnly: boolean;
  readonly onApprovedForProjectOnlyChange: (checked: boolean) => void;
  readonly favoritesOnly: boolean;
  readonly onFavoritesOnlyChange: (checked: boolean) => void;
  readonly searchOpen: boolean;
  readonly onSearchOpen: () => void;
  readonly searchQuery: string;
  readonly onSearchQueryChange: (value: string) => void;
  readonly searchSuggestions: readonly string[];
  readonly searchInputRef: Ref<HTMLInputElement>;
  readonly searchWrapperRef: Ref<HTMLDivElement>;
  readonly onSearchBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

function LibraryFilterBar({
  sectionFilter,
  onSectionFilterChange,
  contextBarDescription,
  activeShotBackTooltip,
  approvedForProjectOnly,
  onApprovedForProjectOnlyChange,
  favoritesOnly,
  onFavoritesOnlyChange,
  searchOpen,
  onSearchOpen,
  searchQuery,
  onSearchQueryChange,
  searchSuggestions,
  searchInputRef,
  searchWrapperRef,
  onSearchBlur
}: LibraryFilterBarProps) {
  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", px: 2, pt: 2, pb: 1, backgroundColor: "background.paper" }}>
        <Chip
          key={VIEW_ALL_CHIP.value}
          label={VIEW_ALL_CHIP.label}
          size="small"
          onClick={() => onSectionFilterChange(VIEW_ALL_CHIP.value)}
          color={sectionFilter === VIEW_ALL_CHIP.value ? "primary" : "default"}
          variant={sectionFilter === VIEW_ALL_CHIP.value ? "filled" : "outlined"}
          sx={{ py: 0.75, px: 1.5 }}
        />
        <Tooltip title={activeShotBackTooltip} arrow>
          <Chip
            label={contextBarDescription}
            size="small"
            onClick={() => onSectionFilterChange("activeShot")}
            color={sectionFilter === "activeShot" ? "primary" : "default"}
            variant={sectionFilter === "activeShot" ? "filled" : "outlined"}
            sx={{
              py: 0.75,
              px: 1.5,
              maxWidth: 168,
              "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
            }}
          />
        </Tooltip>
        {SECTION_FILTER_CHIPS.slice(1).map(({ value, label }) => (
          <Chip
            key={value}
            label={label}
            size="small"
            onClick={() => onSectionFilterChange(value)}
            color={sectionFilter === value ? "primary" : "default"}
            variant={sectionFilter === value ? "filled" : "outlined"}
            sx={{ py: 0.75, px: 1.5 }}
          />
        ))}
      </Box>
      {/* Approved for project (left); Favorites + Search (right) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          px: 2.5,
          pb: 0.5,
          pt: 0,
          minWidth: 0,
          backgroundColor: "background.paper"
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={approvedForProjectOnly}
              onChange={(_, checked) => onApprovedForProjectOnlyChange(checked)}
              sx={{
                "&.Mui-checked": {
                  color: APP_ACTIVE_TOOL_COLOR
                }
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: "text.primary", whiteSpace: "nowrap" }}>
              {searchOpen ? "Approved" : "Approved for project"}
            </Typography>
          }
          sx={{ flexShrink: 0 }}
        />
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, gap: 0.5, justifyContent: "flex-end" }}>
          <Box sx={{ display: searchOpen ? "none" : "flex" }}>
            <Tooltip title={favoritesOnly ? "Showing favorites only" : "Show favorites only"} arrow>
              <IconButton
                size="small"
                aria-label="Favorites"
                aria-pressed={favoritesOnly}
                onClick={() => onFavoritesOnlyChange(!favoritesOnly)}
                sx={{ color: favoritesOnly ? APP_ACTIVE_TOOL_COLOR : "text.primary" }}
              >
                {favoritesOnly ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              width: searchOpen ? 260 : 30,
              overflow: "hidden",
              transition: "width 0.25s ease-in-out",
              flexShrink: 0
            }}
          >
            <Box ref={searchWrapperRef} sx={{ minWidth: 0, display: "flex", width: "100%", justifyContent: "end" }}>
              {searchOpen ? (
                <SearchBox
                  key={`${sectionFilter}-${approvedForProjectOnly}`}
                  inputRef={searchInputRef}
                  onBlur={onSearchBlur}
                  placeholder="Search"
                  value={searchQuery}
                  onChange={onSearchQueryChange}
                  suggestions={searchSuggestions}
                  height={32}
                  sx={{ flex: 1, "& .MuiInputBase-input": { color: "text.primary" } }}
                />
              ) : (
                <Tooltip title="Search" arrow>
                  <IconButton size="small" onClick={onSearchOpen} aria-label="Open search" sx={{ color: "text.primary" }}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default LibraryFilterBar;
